import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Scrolls the window to the top whenever the route pathname changes, including
 * after lazy-loaded route chunks have mounted.
 *
 * Why two scroll calls? With React.lazy + Suspense, the new page may render
 * AFTER the useEffect fires once. We scroll immediately (covers eager routes)
 * AND on the next paint (covers lazy routes whose markup wasn't laid out yet
 * the first time). Hash navigation is preserved so #section anchors still work.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return; // let the browser jump to the anchor
    const doScroll = () => {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      } catch {
        window.scrollTo(0, 0);
      }
      // Older Safari ignores window.scrollTo when CSS scroll-behavior is smooth on html.
      if (document?.documentElement) document.documentElement.scrollTop = 0;
      if (document?.body) document.body.scrollTop = 0;
    };
    doScroll();
    // Run again after the lazy-loaded chunk paints.
    const raf = requestAnimationFrame(doScroll);
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);
  return null;
}
